/**
 *
 * CommunicationDetail
 *
 */

import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { useReactToPrint } from "react-to-print";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Col, Row, Tabs, Form, Empty, notification } from "antd";
const { TabPane } = Tabs;

// icons
import { LoadingOutlined } from "@ant-design/icons";

// components
import Preloader from "components/Preloader";
import DeleteButton from "components/DeleteButton";
// global communication components
import {
  CommunicationPreview,
  CommFormBuilder,
} from "components/Communications";
// inner communication components
import {
  CommunicationHeader,
  CommunicationCopyAudience,
  DiscussionTab,
  AudiencesManagedList,
  CommunicationForm,
  CommunicationReminder,
  FBAppointmentsPlanTab,
  FBFormsPreviewAnswerTab,
} from "pages/Communications/components";

// contexts
import { withTheme } from "engine/Contexts/Theme.context";

// helper for forms
import {
  isEqualFormValues,
  prepearFormValue,
  prepearFormValueRemoveHiddenChain,
} from "utils/forms/forms.utils";
import { getObjId } from "utils/helpers/helper.general";
// hooks
import { useRefState } from "utils/hooks";
// utils
import history, { parseQuery, encodeQueryData } from "utils/history";
import api from "utils/api";
import _local from "utils/localStorageUtils";

export function CommunicationDetail({
  // reducer main props
  mode,
  initialID,
  currentElement,
  setCurrentElement,
  answers,
  // loadings
  loading,
  loadingList,
  loadAnswers,
  // actions
  update,
  copy,
  hardDelete,
  updateActiveElement,
  // appointments plan
  onUpdatePlan,
  loadPlanSuccess,
  loadPlan,
  // form actions and data
  form,
  formFields,
  // contexts
  theme,
}) {

  const { t, i18n } = useTranslation();

  const newKey = "new";
  const localUser = _local.getLocalUser();
  const location = useLocation();
  const commponentToPrintRef = useRef();

  // states for tabs toggle
  const [activeTab, setActiveTab] = useState("general");
  const [tempActiveTab, setTempActiveTab] = useState("general");
  const [loadPrint, setLoadPrint] = useState(false);
  const [stayActiveTab, setStayActiveTab] = useState(false);
  const [formMethod, setFormMethod] = useState("update");
  const [currentMode, setCurrentMode] = useState(mode);
  const [builderFormValid, builderFormValidRef, setBuilderFormValid] = useRefState(true);
  const [showCopyAudienceModal, setShowCopyAudienceModal] = useState(false);

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [isReminderLoading, setIsReminderLoading] = useState(false);

  const [generalForm, generalFormRef, setGeneralForm] = useRefState(
    formFields || [],
  );

  const [updateMarkOnce, setUpdateMarkOnce] = useState(false);
  const [
    updateMarkOnceRuning,
    updateMarkOnceRuningRef,
    setUpdateMarkOnceRuning,
  ] = useRefState(false);

  // Form initialization for each tab
  const [headerForm] = Form.useForm();
  const {
    customForm,
    resetForm,
    submitForm,
    setFormData,
    setFormFieldsValues,
  } = form;

  useEffect(() => {
    if (initialID) {
      setFormMethod("update");

      if (!initialID || initialID == newKey) {
        // Set default data for NEW Item
        setActiveTab("general");
        setFormMethod(newKey);
        setCurrentElement(false);
        // reset data in the header form
        headerForm.resetFields();
        // reset data in the general form
        resetForm();
      } 
    }
    return () => {
      setUpdateMarkOnce(false);
    };
  }, [initialID]);

  // check if initial form fileds is changes
  useEffect(() => {
    setGeneralForm(formFields);
  }, [formFields]);

  // check search params in url
  useEffect(() => {
    const searchParams = parseQuery(location.search) || null;
    if (searchParams) {
      const fromPreview =
        history?.location?.state?.from == "preview_communication";
      if (searchParams.hasOwnProperty("preview")) {
        if (currentElement?.status && currentElement?.status == "sent") {
          setCurrentMode("onlyRead");
        } else {
          setCurrentMode("preview");
          setActiveTab("general");
        }
      } else {
        if (!!currentElement && currentElement?._id) {
          if (currentElement?.status == "sent") {
            setCurrentMode("onlyRead");
          } else {
            setCurrentMode("edit");
            //  setActiveTab('general');
          }
        } else {
          //const formData = customForm?.getFieldsValue();
          setCurrentMode("new");
          setActiveTab("general");
          if (!fromPreview) {
            // reset data in the general form
            headerForm?.resetFields();
            resetForm();
          }
        }
      }
    }
  }, [location.search]);

  // chech 'mode' of viewing of content
  useEffect(() => {
    if (mode) {
      setCurrentMode(mode);
      if (mode == ("edit" || "new" || "preview")) {
        setActiveTab("general");
      }
    }
  }, [mode]);

  // Set data for opened item
  useEffect(() => {
    if (currentElement?._id) {
    
      if (currentElement?.status == "sent") {
        setCurrentMode("onlyRead");
        updateMarksCallback(currentElement);
      } else {
        const searchParams = parseQuery(location.search) || null;
        searchParams.hasOwnProperty("preview")
          ? setCurrentMode("preview")
          : setCurrentMode("edit");
      }

      if (initialID !== currentElement?._id) {
        setActiveTab("general");
      }
      headerForm.setFieldsValue({
        title: currentElement?.title,
      });
    }
  }, [currentElement]);

  // set notifications for 'print' actions
  useEffect(() => {
    if (loadPrint) {
      notification.info({
        duration: 2,
        icon: <LoadingOutlined style={{ color: "#F59C05" }} />,
        message: t("notifications.printing"),
        description: t("notifications.prepearPrinting"),
      });
    }
  }, [loadPrint]);

  // check if we have some changes in the form, and we click outside of this form
  useEffect(() => {
    !!theme?.savedModalOnSaveChanges && saveOutOfChanges();
    !!theme?.canceledModalOnSaveChanges && refreshOutOfChanges();
  }, [theme?.canceledModalOnSaveChanges, theme?.savedModalOnSaveChanges]);
  const refreshOutOfChanges = useCallback(() => {
    setActiveTab(tempActiveTab);
    setStayActiveTab(false);
    // update data in the general form
    setFormData();
  }, [activeTab, tempActiveTab]);
  const saveOutOfChanges = useCallback(async () => {
    if (currentMode == "edit" || currentMode == "new") {
      const headerValidate = await headerForm?.validateFields();
      const formValidate = await customForm?.validateFields();

      if (headerValidate && formValidate) {
        const headerTitle = headerForm?.getFieldValue("title");
        setFormFieldsValues({
          status: "draft",
          title: headerTitle,
        });
        submitForm();
      }
    }
  }, [activeTab, tempActiveTab]);

  const updateMarksCallback = useCallback(
    async (comm, type = "read") => {
      if (!updateMarkOnce && !!comm) {
        const commID = getObjId(comm);
        let runRequest = false;
        if (type == "read") {
          const readership = comm?.readership?.filter(
            el => getObjId(el?.user) == getObjId(localUser),
          );
          (!comm?.readership || readership?.length == 0) && (runRequest = true);
        }
        if (!!runRequest) {
          setUpdateMarkOnceRuning(true);
          if (!updateMarkOnceRuningRef?.current) {
            const res = await api.communications.markAs(commID, type);
            if (res?.status == "ok") {
              setUpdateMarkOnce(true);
              updateActiveElement({
                _id: currentElement?._id,
                readership: res?.readership,
              });
            }
          }
        } else {
          if (type == "appointments" || type == "forms") {
            const res = await api.communications.markAs(commID, type);
            if (res?.status == "ok") {
              updateActiveElement({
                _id: currentElement?._id,
                readershipAppointments: res?.readershipAppointments,
                readershipAppointmentsFiltered:
                  res?.readershipAppointmentsFiltered,
                readershipForms: res?.readershipForms,
                readershipFormsFiltered: res?.readershipFormsFiltered,
              });
            }
          }
        }
      }
    },
    [currentElement, updateMarkOnce, updateMarkOnceRuning],
  );

  const onUpdateMarkCallback = useCallback(
    result => {
      if (result?.readers) {
        updateActiveElement({
          _id: currentElement?._id,
          readers: result?.readers,
          readersFiltered: result?.readersFiltered,
        });
      }
    },
    [currentElement?._id],
  );

  // General Form action
  const onSubmitForm = useCallback(
    data => {
      let updatedData = {
        ...currentElement,
        ...data,
      };
      // Check&Set School as string value (need for backend)
      if (
        updatedData?.audience?.school &&
        Array.isArray(updatedData?.audience?.school) &&
        updatedData?.audience?.school?.length > 0
      ) {
        updatedData.audience.school = updatedData?.audience?.school[0];
      }

      if (!!builderFormValidRef?.current) {
        if (updatedData?.status == "sent" && !updatedData?.sentDate) {
          updatedData.sentDate = new Date().toISOString();
        }
        currentMode !== "onlyRead" && update(updatedData);
        checkFromChanges(false);
        setActiveTab(activeTab);
      } else {
        notification.error({
          duration: 2,
          message: t("errors.attachmentErrorTitle"),
          description: t("errors.attachmentErrorMsg"),
        });
      }
    },
    [currentElement, builderFormValid, currentMode, builderFormValidRef],
  );

  const onSubmitHeaderForm = useCallback(
    async values => {
      const headerValidate = await headerForm?.validateFields();
      const formValidate = await customForm?.validateFields();

      if (
        !!headerValidate &&
        !!formValidate &&
        !!builderFormValidRef?.current
      ) {
        const headerTitle = headerForm?.getFieldValue("title");
        customForm.setFieldsValue({
          status: "sent",
          title: headerTitle,
          sentDate: new Date().toISOString(),
        });
        submitForm();
      } else {
        notification.error({
          duration: 2,
          message:t("errors.attachmentErrorTitle"),
          description: t("errors.attachmentErrorMsg"),
        });
      }
    },
    [currentElement, builderFormValid, currentMode, builderFormValidRef],
  );

  const getPrepearedMainFormValues = useCallback(() => {
    let sendingDataClear = {};
    if (!!customForm) {
      const mainFormValues = customForm?.getFieldsValue();
      const dataSource = generalForm || formFields;
      const sendingData = prepearFormValue(dataSource, mainFormValues);
      sendingDataClear = prepearFormValueRemoveHiddenChain(
        dataSource,
        sendingData,
      );
    }
    return sendingDataClear;
  }, [
    generalForm,
    formFields,
    customForm,
    prepearFormValue,
    prepearFormValueRemoveHiddenChain,
  ]);

  const onFormValuesChange = useCallback(
    (changedValues, allValues) => {
      const allValuesObj = Object.assign(allValues);
      const formVals = Object.values(allValues);
      const formHasAnyVals = formVals.some(
        val => val != "" && val != undefined && val != null,
      );
      const isEqualValuesWithElement = isEqualFormValues(
        generalForm,
        currentElement,
        allValuesObj,
      );

      if (currentElement?.status != "sent") {
        if (currentMode == "edit") {
          if (
            (!isEqualValuesWithElement && !!currentElement) ||
            (!isEqualValuesWithElement && formHasAnyVals && !!!currentElement)
          ) {
            checkFromChanges(true);
          } else {
            checkFromChanges(false);
          }
        } else if (currentMode == "new") {
          if (
            (!isEqualValuesWithElement && !!currentElement) ||
            (!isEqualValuesWithElement && formHasAnyVals && !!!currentElement)
          ) {
            checkFromChanges(true);
          } else {
            checkFromChanges(false);
          }
        }
      }
    },
    [generalForm, currentElement, currentMode],
  );

  const checkBuilderOnChange = useCallback(
    bilderData => {
     // console.log('checkBuilderOnChange', bilderData?.buildFormValid);

      (currentMode == "edit" || currentMode == "new") &&
        setBuilderFormValid(bilderData?.buildFormValid);

      if (
        currentElement?.status == "sent" ||
        currentMode == "onlyRead" ||
        currentMode == "preview" ||
        !!bilderData?.valsEqual
      )
        return checkFromChanges(false);

      if (!bilderData?.valsEqual) {
        if (!!bilderData?.buildFormValid && !!bilderData?.data) {
          const { types, ...restData } = bilderData?.data;
          const newTypes = types || currentElement?.["types"] || ["no-type"];
          const mainFormValues = getPrepearedMainFormValues();
          const newElementStates = {
            ...currentElement,
            ...mainFormValues,
            ...bilderData?.data,
          };
          setFormFieldsValues({ types: newTypes });
          setCurrentElement(newElementStates);
          return checkFromChanges(true);
        } else if (!!!bilderData?.buildFormValid) {
          setBuilderFormValid(bilderData?.buildFormValid);
          return checkFromChanges(true);
        } else {
          return checkFromChanges(false);
        }
      } else {
        return checkFromChanges(false);
      }
    },
    [currentElement, currentMode],
  );

  // Set if form have any changes
  const checkFromChanges = useCallback(haveChange => {
    setStayActiveTab(haveChange);
    theme?.useAdd({
      hasValueModalOnSaveChanges: haveChange,
    });
  }, []);

  /*
   * Сhange the way to switch tabs,
   * if there are changes in the form in the active tab
   */
  const onChangeTab = useCallback(
    (key, event) => {
      setTempActiveTab(key);
      if (!!theme?.hasValueModalOnSaveChanges) {
        event.stopPropagation();
        !stayActiveTab && setActiveTab(key);
        theme?.useAdd({
          isOpenModalOnSaveChanges: true,
          savedModalOnSaveChanges: false,
          canceledModalOnSaveChanges: false,
        });
      } else {
        setActiveTab(key);
      }
      if (key == "appointments" || key == "forms") {
        updateMarksCallback(currentElement, key);
      }
    },
    [currentElement, theme?.hasValueModalOnSaveChanges, stayActiveTab],
  );

  const deleteCallback = useCallback(() => hardDelete(initialID), [initialID]);

  const handlePrint = useReactToPrint({
    onBeforeGetContent: () => {
      setLoadPrint(prev => !prev);
    },
    onBeforePrint: () => {
      setLoadPrint(prev => !prev);
    },
    documentTitle: currentElement?.title,
    content: () => commponentToPrintRef.current,
  });

  const handleMenuClick = useCallback(
    async actionKey => {
      switch (actionKey.key) {
        case "save-draft":
          if (currentMode == "edit" || currentMode == "new") {
            const headerValidate = await headerForm?.validateFields();
            const formValidate = await customForm?.validateFields();

            if (headerValidate && formValidate) {
              const headerTitle = headerForm?.getFieldValue("title");
              setFormFieldsValues({
                status: "draft",
                title: headerTitle,
              });
              submitForm();
            }
          }
          break;

        case "dublicate":
          if (!!initialID && initialID !== newKey && !!copy) {
            copy(initialID);
          }
          break;

        case "reminder":
          if (!!initialID && initialID !== newKey) {
            setShowReminderModal(true);
          }
          break;

        case "delete":
          if (currentMode == "edit" || currentMode == "new") {
            if (initialID && initialID !== newKey) {
              deleteCallback();
            } else if (initialID == newKey) {
              // reset data in the general form
              resetForm();
              headerForm?.resetFields();
              // Set default data for NEW Item
              setCurrentElement(false);
              setActiveTab("general");
              setFormMethod(newKey);
              setFormFieldsValues({
                status: "draft",
              });
              const searchParams = parseQuery(location.search) || null;
              let currentData = {
                ...searchParams,
                preview: false,
              };
              const { page, ...searchData } = currentData;
              const serachStr = encodeQueryData(searchData);
              history.push({
                pathname: `/communications/${newKey}`,
                search: `?${serachStr}`,
                state: {
                  preview: false,
                  params: currentData,
                },
              });
            }
          }
          break;

        case "print":
          handlePrint();
          break;

        case "copy-audience":
          if (!!initialID && initialID !== newKey) {
            setShowCopyAudienceModal(true);
          }
          break;

        case "preview":
          if (currentMode == "edit" || currentMode == "new") {
            const searchParams = parseQuery(location.search) || null;
            let currentData = {
              ...searchParams,
              preview: true,
            };
            const { page, ...searchData } = currentData;
            const serachStr = encodeQueryData(searchData);
            history.push({
              search: `?${serachStr}`,
              state: {
                from: "detail_communication",
                preview: true,
                params: currentData,
              },
            });
          }
          break;
      }
    },
    [currentMode, initialID, location.search, mode],
  );

  /* Appointments Plan Actions */
  const onSaveAppointmentsPlan = useCallback(
    planArray => {
      !!onUpdatePlan && onUpdatePlan(planArray);
    },
    [initialID],
  );

  const onSentAppointmentsPlan = useCallback(
    planArray => {
      !!onUpdatePlan && onUpdatePlan(planArray, "sent");
    },
    [initialID],
  );
  
  const renderCopyAudienceMsg = useMemo(() => { 
    return (initialID != newKey) && (
      <CommunicationCopyAudience
        open={showCopyAudienceModal}
        setOpen={setShowCopyAudienceModal}
      />
    )
  }, [initialID, newKey, showCopyAudienceModal]);
  
  const renderReminder = useMemo(() => { 
    return (mode == "onlyRead" && initialID != newKey) && (
      <CommunicationReminder
        open={showReminderModal}
        setOpen={setShowReminderModal}
        isReminderLoading={!!isReminderLoading}
        setIsReminderLoading={setIsReminderLoading}
      />
    )
  }, [initialID, newKey, mode, showReminderModal, isReminderLoading]);
  
  const renderHeader = useMemo(() => { 
    return (
      <CommunicationHeader
        key={`comm-${initialID}`}
        uniqueKey={`comm-${initialID}`}
        form={headerForm}
        mode={currentMode}
        method={formMethod}
        loadingList={!!loadingList}
        secondaryForm={customForm}
        onValuesChange={onFormValuesChange}
        onSubmit={onSubmitHeaderForm}
        actionDropdownCallback={handleMenuClick}
        types={currentElement?.types}
        senderRole={currentElement?.senderRole}
        isReminderLoading={!!isReminderLoading}
        showReminder={
          currentElement?.students?.length !=
          currentElement?.studentsAnswered?.length
        }
      />
    )
  }, [
    initialID, headerForm, currentMode, formMethod, loadingList, customForm,
    currentElement?.types, currentElement?.senderRole, isReminderLoading,
    currentElement?.students?.length, currentElement?.studentsAnswered?.length
  ]);
  const renderGeneral = useMemo(() => { 
    return (
      <TabPane tab={t("news.generalTab")} key="general" forceRender>
        <Row>
          <Col span={24}>
            <CommunicationForm
              mode={currentMode}
              method={formMethod}
              name={`communication-general-form-${initialID}`}
              communicationDetail={currentElement}
              className="communication-general-form"
              form={customForm}
              dataSource={generalForm || formFields}
              handleSubmit={data => onSubmitForm(data)}
              handleValuesChange={onFormValuesChange}
              labelCol={24}
              wrapperCol={24}
              disabeled={!!!theme?.hasValueModalOnSaveChanges}
              hideSubmit
            />

            <CommFormBuilder
              mode={currentMode}
              method={formMethod}
              saveToForm={customForm}
              onValuesChange={checkBuilderOnChange}
              values={currentElement}
            />

            {currentMode == "onlyRead" && (
              <CommunicationPreview
                ref={commponentToPrintRef}
                mode={currentMode}
                element={currentElement}
                dataSource={formFields}
                hideThumbnail={true}
                mainFormFields={customForm?.getFieldsValue()}
                headFormFields={headerForm?.getFieldsValue()}
                onViewDiscussionClick={() =>
                  setActiveTab("discussion")
                }
              />
            )}
          </Col>
        </Row>
      </TabPane>
    )
  }, [
    currentMode, initialID, formMethod, currentElement, generalForm, formFields, 
    theme?.hasValueModalOnSaveChanges, commponentToPrintRef, i18n?.language
  ]);
  
  const renderTabAudiences = useMemo(() => { 
    return (
      <TabPane
        //disabled={currentElement?.status != 'sent'}
        tab={t("labels.audience")}
        key="audience"
      >
        {initialID && initialID !== newKey && (
          <AudiencesManagedList
            editable={currentMode == "onlyRead"}
            onOpenReminder={setShowReminderModal}
            isReminderLoading={!!isReminderLoading}
          />
        )}
      </TabPane>
    );
  }, [
    initialID, newKey, currentElement?._id, currentMode, isReminderLoading, i18n?.language
    // currentElement?.status
  ]);

  const renderTabAppointments = useMemo(() => { 
    return (
      <TabPane
        key="appointments"
        disabled={
          currentMode != "onlyRead" ||
          !answers?.data?.some(el => !!el?.answer?.appointments)
        }
        tab={
          <>
            {t("global.formTypes.appointments")}
            {!!(
              currentMode == "onlyRead" &&
              answers?.totalAnswersAmount &&
              answers?.data?.some(
                el => !!el?.answer?.appointments,
              ) &&
              answers?.answersAmount
            ) && (
              <span className="amount-tag appointments">
                {`${answers?.answersAmount} / ${
                  answers?.totalAnswersAmount
                }`}
              </span>
            )}
          </>
        }
      >
        {currentMode == "onlyRead" &&
          answers?.data?.[0]?.answer?.appointments && (
            <FBAppointmentsPlanTab
              loading={!!loadAnswers}
              loadPlan={!!loadPlan}
              loadPlanSuccess={!!loadPlanSuccess}
              element={currentElement}
              values={answers}
              onSave={onSaveAppointmentsPlan}
              onSent={onSentAppointmentsPlan}
            />
          )}
      </TabPane>
    )
  }, [
    currentMode, loadAnswers, loadPlan, loadPlanSuccess,
    currentElement, answers?.data, answers?.totalAnswersAmount, initialID, newKey, currentElement?._id, i18n?.language
  ]);
  const renderTabForms = useMemo(() => { 
    return (
      <TabPane
        disabled={
          currentMode != "onlyRead" ||
          !answers?.data?.some(el => !!el?.answer?.forms)
        }
        tab={
          <>
            {t("global.formTypes.form")}
            {!!(
              currentMode == "onlyRead" &&
              answers?.totalAnswersAmount &&
              answers?.data?.some(el => !!el?.answer?.forms) &&
              answers?.answersAmount
            ) && (
              <span className="amount-tag forms">
                {`${answers?.answersAmount} / ${
                  answers?.totalAnswersAmount
                }`}
              </span>
            )}
          </>
        }
        key="forms"
      >
        <FBFormsPreviewAnswerTab
          loading={!!loadAnswers}
          values={answers}
        />
      </TabPane>
    )
  }, [
    currentMode, loadAnswers, loadAnswers,
    answers?.data, answers?.totalAnswersAmount, initialID, newKey, currentElement?._id,
    i18n?.language
  ]);
  const renderTabDiscussion = useMemo(() => { 
    return (
      <TabPane
        disabled={
          currentMode != "onlyRead" || !currentElement?.discussion
        }
        tab={t("global.formTypes.discussion")}
        key="discussion"
      >
        {initialID && initialID !== newKey && (
          <DiscussionTab
            discussionId={currentElement?.discussion}
            onUpdateMark={onUpdateMarkCallback}
          />
        )}
      </TabPane>
    )
  }, [
    currentMode, initialID, newKey, currentElement?.discussion, currentElement?._id,
    i18n?.language
  ]);
  const renderTabSettings = useMemo(() => { 
    return (
      <TabPane
        disabled={currentMode != "onlyRead"}
        tab={t("pages.advancedSettings")}
        key="gestion_avancee"
      >
        {initialID && initialID !== newKey && (
          process.env.BASE_ENV == "development" || process.env.BASE_ENV == "local"
        ) && (
          <DeleteButton
            buttonText={t("news.deleteCommunication")}
            deleteHandle={deleteCallback}
          />
        )}
      </TabPane>
    )
  }, [
    currentMode, initialID, newKey, currentElement?._id, i18n?.language
  ]);

  // render function
  // [antd: Tabs] Tabs.TabPane is deprecated. Please use `items` directly.
  return (
    <div className="details-container">
      {((!initialID && loading) || (loading && !!!currentElement)) && (
        <Preloader />
      )}
      {!loading &&
        ((initialID == newKey && !!!currentElement) ||
          (initialID && !!currentElement)) && (
        <>
          
            {renderCopyAudienceMsg}

            {renderReminder}

            {renderHeader}

            <Row gutter={[0, 0]}>
              <Col xs={24}>
                {currentMode != "preview" && (
                  <Tabs
                    defaultActiveKey={activeTab}
                    activeKey={activeTab}
                    className="details-tabs"
                    onTabClick={onChangeTab}
                  >
                    {renderGeneral}
                    
                    {renderTabAudiences}
                    {renderTabAppointments}
                    {renderTabForms}
                    {renderTabDiscussion}
                    {renderTabSettings}
                  </Tabs>
                )}
                {/* If you've created a component that is intended only for printing and should not render in the parent component, wrap that component in a div with style set to { display: "none" }, like so: */}
                <div
                  style={{
                    display: currentMode != "preview" ? "none" : "block",
                  }}
                >
                  <CommunicationPreview
                    ref={commponentToPrintRef}
                    mode={currentMode}
                    element={currentElement}
                    dataSource={formFields}
                    mainFormFields={customForm?.getFieldsValue()}
                    headFormFields={headerForm?.getFieldsValue()}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      {initialID !== newKey && !loading && !!!currentElement && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("errors.noData")}
        />
      )}
    </div>
  );
}

CommunicationDetail.defaultProps = {
  loading: false,
  mode: "edit",
};
CommunicationDetail.propTypes = {
  mode: PropTypes.oneOf(["edit", "onlyRead", "preview", "new"]).isRequired,
  update: PropTypes.func,
  copy: PropTypes.func,
  loading: PropTypes.bool,
  initialID: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
};

export default compose(
  memo,
  withTheme,
)(CommunicationDetail);
